import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const baseUrl = 'https://terravia-clay-canvas.lovable.app';

// Define schema types as generic objects to avoid TypeScript strict typing issues
type SchemaObject = Record<string, unknown>;

// Organization/Business Schema
const organizationSchema: SchemaObject = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${baseUrl}/#organization`,
  name: 'Terravia Ceramics',
  alternateName: 'Terrravia Ceramics',
  url: baseUrl,
  logo: `${baseUrl}/favicon.png`,
  description: 'Contemporary pottery and ceramic art studio creating handcrafted sculptural vessels and ceramic works.',
  founder: {
    '@type': 'Person',
    name: 'Umar Abdulrasheed',
  },
  address: [
    {
      '@type': 'PostalAddress',
      streetAddress: '116 Miller Height',
      addressLocality: 'Maidstone',
      postalCode: 'ME15 6LZ',
      addressCountry: 'GB',
    },
    {
      '@type': 'PostalAddress',
      streetAddress: 'No 54, Kamaldeen Street, Okekere',
      addressLocality: 'Ilorin',
      addressRegion: 'Kwara State',
      addressCountry: 'NG',
    },
  ],
  email: 'info@terrrevia.com',
  telephone: '+44 7423 202954',
  sameAs: [
    'https://www.instagram.com/terravia_ceramics',
    'https://linkedin.com/in/umar-abdulrasheed-ceramics',
  ],
};

// Artist Person Schema
const artistSchema: SchemaObject = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  '@id': `${baseUrl}/#artist`,
  name: 'Umar Abdulrasheed',
  jobTitle: 'Ceramic Artist',
  description: 'Nigerian-born ceramic sculptor based in London and Creative Director of Terravia Ceramics. His practice explores themes of memory, balance, containment, and quiet presence through hand-built sculptural vessels and abstract forms.',
  url: `${baseUrl}/about`,
  image: `${baseUrl}/og-image.jpg`,
  nationality: [
    { '@type': 'Country', name: 'Nigeria' },
    { '@type': 'Country', name: 'United Kingdom' },
  ],
  alumniOf: [
    {
      '@type': 'EducationalOrganization',
      name: 'University of Hertfordshire',
    },
  ],
  memberOf: [
    { '@type': 'Organization', name: 'Craft Potters Association (UK)' },
    { '@type': 'Organization', name: 'Friends of Burton at Bideford' },
  ],
  award: [
    'Artist of the Year Award - Kwara State University Centre for Entrepreneurship and Craftsmanship (2014)',
    'Artist of the Year Award - Department (2018)',
  ],
  worksFor: {
    '@type': 'Organization',
    name: 'Terravia Ceramics',
  },
  sameAs: [
    'https://www.instagram.com/terravia_ceramics',
    'https://linkedin.com/in/umar-abdulrasheed-ceramics',
  ],
};

// Website Schema
const websiteSchema: SchemaObject = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${baseUrl}/#website`,
  url: baseUrl,
  name: 'Terravia Ceramics',
  description: 'Contemporary pottery and ceramic art shaped by earth and fire',
  publisher: {
    '@id': `${baseUrl}/#organization`,
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${baseUrl}/?search={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

// Artwork schemas for portfolio pieces
const artworkSchemas: SchemaObject[] = [
  {
    '@context': 'https://schema.org',
    '@type': 'VisualArtwork',
    name: 'Paired Silence',
    creator: { '@id': `${baseUrl}/#artist` },
    dateCreated: '2025',
    artMedium: 'Ceramic',
    artform: 'Sculpture',
    description: 'This sculptural pair reflects on coexistence and environmental imbalance, standing as quiet markers of shared vulnerability shaped by pressure, erosion, and climate change.',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'VisualArtwork',
    name: 'Emergent Form',
    creator: { '@id': `${baseUrl}/#artist` },
    dateCreated: '2024',
    artMedium: 'Ceramic',
    artform: 'Sculpture',
    description: 'This small-scale sculptural work explores growth and transformation through an abstracted organic form.',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'VisualArtwork',
    name: 'Quiet Alignment',
    creator: { '@id': `${baseUrl}/#artist` },
    dateCreated: '2023',
    artMedium: 'Ceramic',
    artform: 'Sculpture',
    description: 'This paired sculptural work explores relationship and proximity through two vertically oriented forms.',
  },
];

// Exhibition Event schemas
const exhibitionSchemas: SchemaObject[] = [
  {
    '@context': 'https://schema.org',
    '@type': 'ExhibitionEvent',
    name: 'Soul in Motion',
    location: {
      '@type': 'Place',
      name: 'Espacio Gallery',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'London',
        addressCountry: 'GB',
      },
    },
    startDate: '2025',
    performer: { '@id': `${baseUrl}/#artist` },
    organizer: { '@type': 'Organization', name: 'Espacio Gallery' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ExhibitionEvent',
    name: 'Earthen Echoes - Debut Solo Ceramic Exhibition',
    location: {
      '@type': 'Place',
      name: 'Orchard Light Gallery',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Ilorin',
        addressCountry: 'NG',
      },
    },
    startDate: '2021',
    performer: { '@id': `${baseUrl}/#artist` },
  },
];

// Breadcrumb schemas per page
const getBreadcrumbSchema = (pathname: string): SchemaObject => {
  const breadcrumbs: Record<string, { name: string; url: string }[]> = {
    '/': [{ name: 'Home', url: baseUrl }],
    '/portfolio': [
      { name: 'Home', url: baseUrl },
      { name: 'Portfolio', url: `${baseUrl}/portfolio` },
    ],
    '/about': [
      { name: 'Home', url: baseUrl },
      { name: 'About', url: `${baseUrl}/about` },
    ],
    '/exhibitions': [
      { name: 'Home', url: baseUrl },
      { name: 'Exhibitions', url: `${baseUrl}/exhibitions` },
    ],
    '/news': [
      { name: 'Home', url: baseUrl },
      { name: 'News', url: `${baseUrl}/news` },
    ],
    '/contact': [
      { name: 'Home', url: baseUrl },
      { name: 'Contact', url: `${baseUrl}/contact` },
    ],
    '/artist-cv': [
      { name: 'Home', url: baseUrl },
      { name: 'Artist CV', url: `${baseUrl}/artist-cv` },
    ],
  };

  const items = breadcrumbs[pathname] || breadcrumbs['/'];
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
};

const getPageSchemas = (pathname: string): SchemaObject[] => {
  const schemas: SchemaObject[] = [organizationSchema, websiteSchema, getBreadcrumbSchema(pathname)];
  
  if (pathname === '/about') {
    schemas.push(artistSchema);
  }
  
  if (pathname === '/portfolio') {
    schemas.push(...artworkSchemas);
  }
  
  if (pathname === '/exhibitions') {
    schemas.push(...exhibitionSchemas);
  }
  
  return schemas;
};

export const StructuredData = () => {
  const location = useLocation();

  useEffect(() => {
    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll('script[data-structured-data]');
    existingScripts.forEach(script => script.remove());

    // Add new structured data
    const schemas = getPageSchemas(location.pathname);
    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-structured-data', `schema-${index}`);
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      const scripts = document.querySelectorAll('script[data-structured-data]');
      scripts.forEach(script => script.remove());
    };
  }, [location.pathname]);

  return null;
};

export default StructuredData;
